/**
 * Reddit Pixel helper for the customer dashboard (app.mediloop.ai).
 *
 * Mirrors apps/web's marketing-side Reddit Pixel — the marketing snippet
 * fires PageVisit + ViewContent + Lead + AddToCart, this one fires the
 * downstream Purchase event after Stripe success so Reddit Ads can
 * optimize on real conversions.
 *
 * Reddit's standard event taxonomy is smaller than Meta's:
 *   PageVisit, ViewContent, Search, AddToCart, AddToWishlist,
 *   Lead, SignUp, Purchase, Custom
 *
 * Meta's `InitiateCheckout` maps to Reddit's `AddToCart` per Reddit's docs.
 *
 * Env vars:
 *   VITE_REDDIT_PIXEL_ID — overrides the default ID. Set to "" in dev/staging
 *                          to disable. Defaults to the mediloop production
 *                          pixel ID below if unset.
 */

import { isBlockedTrackingHost } from './fbpixel';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    rdt?: ((...args: any[]) => void) & {
      callQueue?: any[];
      sendEvent?: (...args: any[]) => void;
    };
  }
}

// Hardcoded default — the live Reddit Pixel for mediloop. Lifted to a
// constant so swapping pixels (e.g. for a separate Reddit ad account)
// is a one-line change. Empty-string env override disables the pixel.
const DEFAULT_PIXEL_ID = 'a2_iv7di5wrox7t';
const RAW_PIXEL_ID = import.meta.env.VITE_REDDIT_PIXEL_ID as string | undefined;
const PIXEL_ID = RAW_PIXEL_ID === undefined ? DEFAULT_PIXEL_ID : RAW_PIXEL_ID;

/**
 * Load the Reddit Pixel snippet and fire PageVisit. Idempotent — repeated
 * calls are no-ops once the pixel is loaded. Call once at app startup
 * from main.tsx, same place initPixel() (Meta) lives.
 */
export function initRedditPixel(): void {
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined') return;
  // Never run on staging or local-dev hosts (2026-07-06). This pixel
  // DEFAULTS to the production ID when VITE_REDDIT_PIXEL_ID is unset, so
  // without this guard every `vite dev` session fired production PageVisits.
  if (isBlockedTrackingHost()) return;
  if (typeof window.rdt === 'function') return;

  // Reddit's canonical pixel snippet, rewritten in TS-friendly form.
  // Pushes calls to a queue until pixel.js loads, then flushes via
  // sendEvent. Same behavior as the inline <script> on mediloop.ai.
  const w = window as any;
  const p: any = function (...args: any[]) {
    p.sendEvent ? p.sendEvent.apply(p, args) : p.callQueue.push(args);
  };
  p.callQueue = [];
  w.rdt = p;

  const tag = document.createElement('script');
  tag.src = 'https://www.redditstatic.com/ads/pixel.js';
  tag.async = true;
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(tag, first);

  w.rdt('init', PIXEL_ID);
  w.rdt('track', 'PageVisit');
}

/**
 * Advanced matching — pass identifying signals to Reddit so it can match
 * conversions to Reddit users. This is what lifts the "Match key coverage"
 * (Email / ExternalID) in Events Manager off 0%.
 *
 * Reddit reads advanced-matching params from `rdt('init', ...)`. Re-calling
 * init with these once we know the user (post-login / at conversion time)
 * updates the matching for subsequent events without reloading pixel.js.
 * Reddit hashes the plaintext email client-side, so we pass it raw.
 */
interface AdvancedMatching {
  email?: string;
  externalId?: string;
}

function applyAdvancedMatching({ email, externalId }: AdvancedMatching): void {
  if (typeof window.rdt !== 'function') return;
  if (!email && !externalId) return;
  const am: Record<string, unknown> = {};
  if (email) am.email = email;
  if (externalId) am.externalId = String(externalId);
  window.rdt('init', PIXEL_ID, am);
}

interface PurchaseParams {
  value: number;
  currency?: string;
  /** Plan slug: 'one_off' | 'annual' | 'eob' | 'addon_bill' | 'upgrade_to_annual' | 'eob_to_one_off' */
  contentName: string;
  /** Unique transaction ID (checkout_ref or Stripe session_id). Dedups refresh re-fires. */
  eventID: string;
  /** Advanced matching — raw email (Reddit hashes it) for conversion matching. */
  email?: string;
  /** Advanced matching — stable user id (Supabase user.id). */
  externalId?: string;
}

/**
 * Fire a Reddit Purchase event with deduplication.
 *
 * Uses sessionStorage to prevent double-fires on page refresh — same
 * pattern as trackPurchase() in fbpixel.ts so Reddit and Meta stay in
 * lockstep when both pixels load.
 *
 * No-op if pixel is not loaded (ad blockers, missing ID).
 */
export function trackRedditPurchase({
  value,
  currency = 'USD',
  contentName,
  eventID,
  email,
  externalId,
}: PurchaseParams): void {
  // 🧪 Guard (2026-07-02): cs_test_* = Stripe test mode — skip real
  // Reddit Purchase fires for QA checkouts (mirrors webhook livemode gate).
  if (eventID?.startsWith('cs_test_')) return;

  const storageKey = `rdt_purchase_tracked_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // sessionStorage unavailable (private browsing) — continue anyway
  }

  if (typeof window.rdt !== 'function') return;

  applyAdvancedMatching({ email, externalId });

  window.rdt('track', 'Purchase', {
    value,
    currency,
    itemCount: 1,
    conversionId: eventID,
    products: [{ id: contentName, name: contentName, category: 'plan' }],
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Silently fail if storage is full/unavailable
  }
}

/**
 * Fire a Reddit AddToCart event (Reddit's equivalent of Meta's
 * InitiateCheckout — used when the user starts a Stripe Checkout flow).
 */
export function trackRedditAddToCart(params: {
  eventID: string;
  value: number;
  currency?: string;
  contentName: string;
  email?: string;
  externalId?: string;
}): void {
  const { eventID, value, currency = 'USD', contentName, email, externalId } = params;
  const storageKey = `rdt_add_to_cart_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // ignore
  }

  if (typeof window.rdt !== 'function') return;

  applyAdvancedMatching({ email, externalId });

  window.rdt('track', 'AddToCart', {
    value,
    currency,
    itemCount: 1,
    conversionId: eventID,
    products: [{ id: contentName, name: contentName, category: 'plan' }],
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}

/**
 * Fire a Reddit SignUp event (Meta's CompleteRegistration equivalent).
 */
export function trackRedditSignUp(params: {
  eventID: string;
  email?: string;
  externalId?: string;
}): void {
  const { eventID, email, externalId } = params;
  const storageKey = `rdt_signup_tracked_${eventID}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // ignore
  }

  if (typeof window.rdt !== 'function') return;
  applyAdvancedMatching({ email, externalId });
  window.rdt('track', 'SignUp', { conversionId: eventID });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}
